/**
 * whatsapp-cta.js — lógica dos CTAs de WhatsApp da Promacal.
 *
 * Duas metades:
 *  1. Funções puras (testadas em tests/whatsapp-cta.test.js via `node --test`)
 *  2. Wiring de DOM, que só roda no navegador (guardado por `typeof document`)
 *
 * O número do WhatsApp é definido no snippets/whatsapp-cta.liquid e injetado
 * aqui via window.PROMACAL_WA_NUMERO. O fallback abaixo existe só para o teste.
 */
(function (root) {
  'use strict';

  var NUMERO_FALLBACK = '5547996930011';
  var MAX_CM = 280;
  var MAX_M_TEXTO = '2,80';

  // Camada 1 do rastreamento: o código que identifica a origem da conversa.
  var CODIGOS = {
    medida_a: 'p1',
    medida_b: 'p1',        // mesmo código: é o mesmo momento, braços diferentes do A/B
    pos_compra_btn: 'p2',
    carrinho: 'c3',
    colecao: 'cl',
    pagina_404: '404',
    altura_acima_max_erro: 'h1',
    altura_acima_max_aviso: 'h2',
    fab: 'fb',
    header: 'hd',
    rodape: 'rd',
    quiz_acima_6m: 'w6',
    outro: 'ou',
  };

  function numero() {
    return (typeof root !== 'undefined' && root.PROMACAL_WA_NUMERO) || NUMERO_FALLBACK;
  }

  /**
   * "320" -> 320 | "3,20" -> 320 | "abc" -> null | "-5" -> null
   *
   * A heurística `num > 10 ? num : num*100` é a do código atual em height-input.liquid
   * e quiz-personalizacao.liquid, preservada exatamente — é ela que faz "3,2" virar 320
   * e "320" continuar 320.
   *
   * A rejeição do sinal negativo é a única mudança de comportamento: no código atual o
   * `replace(/[^\d.]/g,'')` apagava o "-", então "-5" virava 5 e depois 500 cm. Sem efeito
   * prático (500 > 280, o add_to_cart era bloqueado de qualquer forma), mas gerava uma
   * mensagem absurda de "5,00 m de altura" no CTA novo.
   */
  function parseHeightToCm(raw) {
    if (typeof raw !== 'string') return null;
    var limpo = raw.trim();
    if (limpo.charAt(0) === '-') return null;
    var normalized = limpo.replace(',', '.').replace(/[^\d.]/g, '');
    var num = parseFloat(normalized);
    if (isNaN(num) || num <= 0) return null;
    return num > 10 ? num : Math.round(num * 100);
  }

  /** 320 -> "3,20" */
  function formatMeters(cm) {
    return (cm / 100).toFixed(2).replace('.', ',');
  }

  function medidasEntreParenteses(dados) {
    var partes = [];
    if (dados.larguraM) partes.push(dados.larguraM + ' m');
    if (dados.alturaM) partes.push(dados.alturaM + ' m');
    if (partes.length === 0) return '';
    return ' (' + partes.join(' × ') + ')';
  }

  var TEXTOS = {
    product_medida: function (d) {
      return 'Olá! Preciso de ajuda para medir meu ambiente. Estou vendo a ' + d.produto + '.';
    },
    product: function (d) {
      return 'Olá! Estou vendo a ' + d.produto + medidasEntreParenteses(d) + ' e tenho uma dúvida.';
    },
    cart: function (d) {
      var plural = d.itens === 1 ? '1 item' : d.itens + ' itens';
      return 'Olá! Tenho ' + plural + ' no carrinho (' + d.valor + ') e uma dúvida antes de finalizar.';
    },
    collection: function (d) {
      return 'Olá! Estou escolhendo entre os modelos de ' + d.colecao + ' e queria ajuda.';
    },
    '404': function () {
      return 'Olá! Cliquei num link que não abriu, podem me ajudar a achar o produto?';
    },
    altura_erro: function (d) {
      return 'Olá! Preciso de uma ' + d.produto + ' com ' + d.alturaM +
        ' m de altura (acima de ' + MAX_M_TEXTO + ' m). Podem montar um orçamento?';
    },
    altura_aviso: function (d) {
      return 'Olá! Preciso de uma ' + (d.produto || 'cortina') + ' com altura acima de ' +
        MAX_M_TEXTO + ' m. Podem montar um orçamento?';
    },
    largura_acima_max: function (d) {
      return 'Olá! Preciso de uma ' + (d.produto || 'cortina') +
        ' com largura acima de 6,00 m. Podem me orientar sobre os 2 painéis?';
    },
    generico: function () {
      return 'Olá! Tenho uma dúvida sobre as cortinas sob medida.';
    },
  };

  /** Monta a mensagem completa, já com o código da camada 1 no fim. */
  function buildMessage(contexto, dados) {
    dados = dados || {};
    var fn = TEXTOS[contexto] || TEXTOS.generico;
    var codigo = CODIGOS[dados.location] || CODIGOS.outro;
    return fn(dados) + ' [' + codigo + ']';
  }

  function waUrl(mensagem) {
    return 'https://wa.me/' + numero() + '?text=' + encodeURIComponent(mensagem);
  }

  var api = {
    CODIGOS: CODIGOS,
    MAX_CM: MAX_CM,
    MAX_M_TEXTO: MAX_M_TEXTO,
    parseHeightToCm: parseHeightToCm,
    formatMeters: formatMeters,
    buildMessage: buildMessage,
    waUrl: waUrl,
    mostrarCtaOvermax: mostrarCtaOvermax,
    esconderCtaOvermax: esconderCtaOvermax,
  };

  // ---- wiring de DOM: só no navegador ----
  function dispararEvento(params) {
    root.dataLayer = root.dataLayer || [];
    root.dataLayer.push(Object.assign({ event: 'whatsapp_click' }, params));
    if (root.gtag) root.gtag('event', 'whatsapp_click', params);
  }

  /**
   * Lê a altura digitada na tela. Dois seletores porque os braços do A/B usam campos diferentes:
   *  - variante A: blocks/height-input.liquid → id="HeightInput-<product.id>"
   *  - variante B: blocks/quiz-personalizacao.liquid → [data-height-input]
   */
  function alturaNaTela() {
    var el = document.querySelector('[data-height-input], input[id^="HeightInput-"]');
    if (!el || !el.value) return null;
    var cm = parseHeightToCm(el.value);
    return cm ? formatMeters(cm) : null;
  }

  /**
   * Lê a largura escolhida. Fonte primária é o atributo que o Liquid injeta no próprio CTA
   * (`data-wa-variante` = título da variante selecionada, que contém a largura, ex. "3,00 m").
   * O Flora re-renderiza a seção ao trocar de variante, então o atributo acompanha a escolha.
   * Fallback: o radio marcado do seletor de variante (snippets/variant-swatches.liquid usa
   * name terminando em "-swatch" e value com o valor da opção).
   */
  function larguraNaTela(el) {
    var texto = (el && el.getAttribute('data-wa-variante')) || '';
    if (!texto) {
      var marcado = document.querySelector('input[name$="-swatch"]:checked');
      texto = marcado ? marcado.value || '' : '';
    }
    var achou = String(texto).match(/(\d+[,.]\d{2})\s*m/);
    return achou ? achou[1].replace('.', ',') : null;
  }

  /** Junta as duas, omitindo o que não existir. */
  function medidasNaTela(el) {
    var out = {};
    var largura = larguraNaTela(el);
    var altura = alturaNaTela();
    if (largura) out.larguraM = largura;
    if (altura) out.alturaM = altura;
    return out;
  }

  function aoClicar(el) {
    var location = el.getAttribute('data-wa-location') || 'outro';
    var contexto = el.getAttribute('data-wa-contexto') || 'generico';

    // Reescreve o href com as medidas atuais (o Liquid já pôs a versão sem medidas).
    if (contexto === 'product') {
      var dados = medidasNaTela(el);
      dados.location = location;
      dados.produto = el.getAttribute('data-wa-produto') || 'cortina';
      el.setAttribute('href', waUrl(buildMessage('product', dados)));
    }

    dispararEvento({
      location: location,
      page_type: el.getAttribute('data-wa-page') || 'desconhecido',
      product_handle: el.getAttribute('data-wa-handle') || undefined,
      ab_variant: (document.cookie.match(/pmc_ab=([ab])/) || [])[1] || undefined,
    });
  }

  /** Listener delegado: cobre os CTAs do snippet E qualquer link wa.me solto (header/rodapé/Customizer). */
  function inferirLocation(el) {
    if (el.closest('header')) return 'header';
    if (el.closest('footer')) return 'rodape';
    return 'outro';
  }

  function init() {
    document.addEventListener('click', function (ev) {
      var el = ev.target.closest && ev.target.closest('a[href*="wa.me"]');
      if (!el) return;
      if (el.hasAttribute('data-wa-tracked')) {
        aoClicar(el);
      } else {
        dispararEvento({
          location: inferirLocation(el),
          page_type: (document.body.getAttribute('data-template') || 'desconhecido'),
        });
      }
    }, { passive: true });
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  /**
   * Mostra o CTA de orçamento para altura acima do máximo, com a altura digitada na mensagem.
   * @param {number} cm - altura informada, em centímetros
   * @param {Element} [escopo] - onde procurar o wrapper (o quiz passa a si mesmo; a
   *                             variante A omite e busca no documento)
   */
  function mostrarCtaOvermax(cm, escopo) {
    var raiz = escopo || document;
    var wrapper = raiz.querySelector('[data-height-overmax]');
    if (!wrapper) return;
    var link = wrapper.querySelector('[data-wa-cta]');
    if (link) {
      link.setAttribute('href', waUrl(buildMessage('altura_erro', {
        location: 'altura_acima_max_erro',
        produto: link.getAttribute('data-wa-produto') || 'cortina',
        alturaM: formatMeters(cm),
      })));
    }
    wrapper.hidden = false;
  }

  function esconderCtaOvermax(escopo) {
    var raiz = escopo || document;
    var wrapper = raiz.querySelector('[data-height-overmax]');
    if (wrapper) wrapper.hidden = true;
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof root !== 'undefined') root.PromacalWA = api;
})(typeof globalThis !== 'undefined' ? globalThis : this);
